import React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { getLanguage, getMarket } from '@travel/i18n';
import { ArrowRight } from '@travel/icons/ui';
import { cx, isEmptyArray } from '@travel/utils';

import { getHost } from 'store/__router/selectors';

import { PlaceLayer } from 'AreaDetails-Types';
import { PLACE_NAVIGATION } from 'constants/provider';
import { Translate } from 'core/translate';
import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';

import { isCountryLevelPathId } from '../../utils/areaPage';

import styles from './breadcrumb.module.scss';

type Props = {
  className?: string;
  /** Array of place layers */
  placeLayers: Array<PlaceLayer>;
};

export const generateJSONLD = (basePath: string, placeLayers: Array<PlaceLayer>) => {
  const layers = placeLayers.map((place, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: place.name,
    item: place.pathId ? `${basePath}${paths.providerList.pathResolver(place.pathId)}` : undefined,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: layers,
  };
};

function Breadcrumb(props: Props) {
  const host = useSelector(getHost);
  const { marketCode } = useSelector(getMarket);
  const language = useSelector(getLanguage);

  if (isEmptyArray(props.placeLayers)) {
    return null;
  }

  const basePath = `https://${host}/${marketCode}/${language}`.toLowerCase();
  const arrowIcon = <ArrowRight className={styles.arrowRightIcon} size={10} />;

  return (
    <>
      {/* Structured Data (See more: https://developers.google.com/search/docs/advanced/structured-data/breadcrumb) */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateJSONLD(basePath, props.placeLayers))}
        </script>
      </Helmet>

      <div className={cx(styles.wrapper, props.className)} data-testid="breadcrumb-wrapper">
        {/* TODO: Replace with label https://jira.rakuten-it.com/jira/browse/TLM-367 */}
        <Link to={paths.top.pathResolver()}>
          <Translate className={styles.component} id="Traveler_Common.Breadcrumb_Home" />
        </Link>
        {arrowIcon}
        {props.placeLayers.map((place, index) => {
          const isBottomPlace = index === props.placeLayers.length - 1;

          /* last layer or country layer (Japan) */
          if (!place.pathId || isCountryLevelPathId(place.pathId)) {
            return (
              <React.Fragment key={place.name}>
                <span className={styles.component} data-testid="breadcrumb-unlink-place">
                  {place.name}
                </span>
                {!isBottomPlace && arrowIcon}
              </React.Fragment>
            );
          }

          const pathResolver =
            place.navigatePage === PLACE_NAVIGATION.AREA
              ? paths.area.pathResolver
              : paths.providerList.pathResolver;

          return (
            <React.Fragment key={place.pathId}>
              <Link to={pathResolver(place.pathId)}>
                <span className={styles.component}>{place.name}</span>
              </Link>
              {!isBottomPlace && arrowIcon}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

export default Breadcrumb;
